import { makeAutoObservable, observable, reaction, runInAction } from 'mobx';

import { isFlagKeyEnabled } from '@/features/feature-flags';
import { getFeatureFlagOverride } from '@/features/feature-flags/feature-flags';
import { logError } from '@/lib/errorLogging';
import { removeDeviceSetting } from '@/utils/localStorage';

import type {
  PluginDefinition,
  PluginMessageName,
  PluginPage,
} from './define-plugin';

export type PluginModule = { default: PluginDefinition };

/** Each plugin's chunk by its entry file's path, as `import.meta.glob` lists them */
export type PluginLoaders = Record<string, () => Promise<PluginModule>>;

// The snake_case folder's kebab form names the flag `plugin-<id>` and the route `/p/<id>`
const pluginIdOf = (entryPath: string) =>
  entryPath.split('/').at(-3)!.replaceAll('_', '-');

const pluginFlagKey = (id: string) => `plugin-${id}`;

const isPluginEnabled = (id: string) => isFlagKeyEnabled(pluginFlagKey(id));

/** Why a plugin is on or off for this user: their own switch on this computer wins over the server's flag */
export type PluginFlagState = 'server' | 'switched-on' | 'switched-off' | 'off';

const pluginFlagStateOf = (flagKey: string): PluginFlagState => {
  // Read the flag first, even when an override decides the state: it is the observable that reruns readers
  const isEnabled = isFlagKeyEnabled(flagKey);
  const override = getFeatureFlagOverride(flagKey);
  if (override !== undefined) return override ? 'switched-on' : 'switched-off';
  return isEnabled ? 'server' : 'off';
};

const plainWordsOf = (id: string) =>
  id.charAt(0).toUpperCase() + id.slice(1).replaceAll('-', ' ');

class PluginsStore {
  private loaders = new Map<string, () => Promise<PluginModule>>();
  private readonly loads = new Map<string, Promise<void>>();
  private readonly definitions = observable.map<string, PluginDefinition>();
  // Plugins whose chunk failed to load. They count as not loading, and load again only after their flag
  // turns off and back on
  private readonly failed = observable.set<string>();

  constructor() {
    makeAutoObservable<this, 'loaders' | 'loads'>(this, {
      loaders: observable.ref,
      loads: false,
    });
  }

  /** Loads each plugin's chunk once its flag is on, as flags load and change, until the returned function runs */
  start(loaders: PluginLoaders): () => void {
    // The Feed's view switch is gone, so the view a user picked in it has no reader
    removeDeviceSetting('feed-view');
    this.loaders = new Map(
      Object.entries(loaders).map(([entryPath, load]) => [
        pluginIdOf(entryPath),
        load,
      ])
    );
    return reaction(
      () => this.enabledIds,
      (ids) => {
        for (const id of this.failed)
          if (!ids.includes(id)) this.failed.delete(id);
        ids.forEach((id) => void this.load(id));
      },
      { fireImmediately: true }
    );
  }

  // Derived from the flags on every read, so a plugin whose flag turns off drops out of every slot at once
  private get enabledIds(): string[] {
    return [...this.loaders.keys()].filter(isPluginEnabled);
  }

  /** Every plugin the host found, enabled or not. Its chunk loads only while it is on, so until then its name is its id */
  get found() {
    return [...this.loaders.keys()].map((id) => {
      const definition = this.definitions.get(id);
      const flagKey = pluginFlagKey(id);
      return {
        id,
        flagKey,
        label: definition?.page?.label ?? plainWordsOf(id),
        state: pluginFlagStateOf(flagKey),
      };
    });
  }

  private slot<K extends keyof PluginDefinition>(key: K) {
    return this.enabledIds.flatMap((id) => {
      const contribution = this.definitions.get(id)?.[key];
      return contribution ? [{ id, ...contribution }] : [];
    });
  }

  get pages() {
    return this.slot('page');
  }

  get homeSections() {
    return this.slot('homeSection');
  }

  /** Every enabled plugin that handles a message, by id */
  handlersOf<Name extends PluginMessageName>(name: Name) {
    return [...this.enabledIds].sort().flatMap((id) => {
      const handle = this.definitions.get(id)?.handles?.[name];
      return handle ? [{ id, handle }] : [];
    });
  }

  /**
   * Settles when the enabled plugins still loading change: one loads or fails, or a flag turns one on or off.
   * Null when none is loading. A plugin that failed to load is not loading. Aborting `signal` settles it at
   * once and stops it watching, so a dropped message leaves nothing behind while a load stalls
   */
  whenLoadingChanges(signal?: AbortSignal): Promise<void> | null {
    const loadingNow = () =>
      this.enabledIds.filter(
        (id) => !this.definitions.has(id) && !this.failed.has(id)
      );
    const loading = loadingNow();
    if (loading.length === 0) return null;
    return new Promise((resolve) => {
      const settle = () => {
        stopWatching();
        signal?.removeEventListener('abort', settle);
        resolve();
      };
      const stopWatching = reaction(() => loadingNow().join(' '), settle, {
        name: 'a plugin message waiting for plugins to load',
      });
      signal?.addEventListener('abort', settle);
      for (const id of loading) void this.load(id).then(settle, settle);
    });
  }

  isEnabled(id: string): boolean {
    return isPluginEnabled(id);
  }

  pageOf(id: string): PluginPage | undefined {
    return this.pages.find((page) => page.id === id);
  }

  /** Settles once an enabled plugin's chunk has loaded, for a route opened before the host loaded it */
  load(id: string): Promise<void> {
    const loader = this.loaders.get(id);
    if (!loader || !isPluginEnabled(id) || this.failed.has(id))
      return Promise.resolve();
    const pending = this.loads.get(id);
    if (pending) return pending;

    const load = loader().then(
      (module) => {
        runInAction(() => this.definitions.set(id, module.default));
      },
      (error: unknown) => {
        logError(error, `Plugin ${id} failed to load`, {
          context: 'plugins',
          metadata: { pluginId: id },
        });
        runInAction(() => {
          this.loads.delete(id);
          this.failed.add(id);
        });
      }
    );
    this.loads.set(id, load);
    return load;
  }
}

export const pluginsStore = new PluginsStore();
